/**
 * @lodex-pro/web-shared/api/use-api-query — typed TanStack Query hook over authedFetch.
 *
 * Phase 9 / Plan 09-03 — wraps `useQuery` from `@tanstack/react-query` with the
 * lodeX Pro authedFetch transport. Feature hooks (use-jobs.ts, use-driver-positions.ts,
 * etc.) call this instead of bare `useQuery`.
 *
 * CRITICAL CONSTRAINT (T-09-03-01 / RESEARCH Pattern 1): `authedFetch` already handles
 * 401-refresh-retry (Plan 04-11 CR-06 fix). This hook MUST NOT add its own 401/refresh
 * logic — a second refresh path would cause double-refresh token races.
 *
 * `queryKey` is NOT omitted from the override type so callers can pass array keys that
 * prevent cache collisions between feature hooks (RESEARCH Pitfall 4). Defaults to
 * `[path]` only when not provided.
 *
 * Spec: CONTEXT D-06; RESEARCH Pattern 1 (no-double-401) + Pitfall 4 (queryKey collisions).
 */

import { useQuery, type QueryKey, type UseQueryResult } from '@tanstack/react-query';
import { authedFetch, type WebApiOpts } from '../auth/api-client.js';
import { apiErrorFromResponse } from './api-error.js';

/**
 * Options for useApiQuery — `queryKey` is optional (defaults to `[path]`).
 * `queryFn` is managed by the hook and must not be overridden.
 * All other TanStack Query options are supported.
 */
export interface ApiQueryOptions<T> {
  /** Override the default `[path]` cache key. Use array keys to prevent cache collisions. */
  readonly queryKey?: QueryKey;
  /** Number of times to retry a failed query (default: inherits QueryClient default of 1). */
  readonly retry?: number | boolean;
  /** Milliseconds before data becomes stale (default: 25_000 from QueryClient default). */
  readonly staleTime?: number;
  /** Milliseconds between background refetches for polling hooks. */
  readonly refetchInterval?: number | false;
  /** Whether to refetch when tab regains focus. */
  readonly refetchOnWindowFocus?: boolean;
  /** Whether to refetch at `refetchInterval` when the tab is hidden. */
  readonly refetchIntervalInBackground?: boolean;
  /** Set to false to disable the query without removing cached data. */
  readonly enabled?: boolean;
  /** Callback invoked on successful query. */
  readonly onSuccess?: (data: T) => void;
  /** Select/transform the query data before it is returned. */
  readonly select?: (data: T) => T;
}

/**
 * Typed wrapper around `useQuery` that uses `authedFetch` as the fetch transport.
 *
 * @param opts     API configuration (base URL) — from `useApiOpts()` or passed directly.
 * @param path     Absolute API path, e.g. `'/v1/jobs'`. Used as the default query key.
 * @param queryOptions  TanStack Query options. `queryFn` is managed by this hook and
 *                      must not be overridden. `queryKey` defaults to `[path]`; pass
 *                      an array key to scope the cache entry to specific parameters
 *                      (RESEARCH Pitfall 4 — prevents cache collisions).
 */
export function useApiQuery<T>(
  opts: WebApiOpts,
  path: string,
  queryOptions?: ApiQueryOptions<T>,
): UseQueryResult<T> {
  const {
    queryKey,
    retry,
    staleTime,
    refetchInterval,
    refetchOnWindowFocus,
    refetchIntervalInBackground,
    enabled,
    select,
  } = queryOptions ?? {};

  return useQuery<T>({
    queryKey: queryKey ?? [path],
    // OPEN-194: TanStack's `signal` goes to the fetch, so a request nobody waits for any more (a
    // search box's previous text, a page left mid-load) is aborted instead of running to the end
    // beside the one that replaced it. Reading `signal` is what opts the query in to that.
    queryFn: async ({ signal }): Promise<T> => {
      const res = await authedFetch(opts, path, { signal });
      if (!res.ok) {
        // Read the error body ONCE (only here — the success path below reads it
        // instead) to recover the API error code, so callers can branch on it
        // structurally instead of substring-matching the status out of the
        // message. Best-effort: non-JSON responses (proxy/gateway errors) leave
        // code = null. `message` stays byte-identical to the old bare-Error
        // format for backward compatibility.
        throw await apiErrorFromResponse(res, path);
      }
      return res.json() as Promise<T>;
    },
    ...(retry !== undefined && { retry }),
    ...(staleTime !== undefined && { staleTime }),
    ...(refetchInterval !== undefined && { refetchInterval }),
    ...(refetchOnWindowFocus !== undefined && { refetchOnWindowFocus }),
    ...(refetchIntervalInBackground !== undefined && { refetchIntervalInBackground }),
    ...(enabled !== undefined && { enabled }),
    ...(select !== undefined && { select }),
  });
}
